import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient, SubscriptionClient, fromSatoshi } from "../sdk";
import { ConnectionState } from "../sdk/types/transport";

export interface BucketCollection {
  bucketId: string;
  marginType: string; // e.g., 'Isolated', 'Cross'
  maintenanceMargin: number;
  nominalValue: number;
  riskRatio: number; // e.g., 0.75 for 75%
}

export interface BucketCollectionsConfig {
  baseURL: string;
  wsURL?: string; // WebSocket URL (required if useWebSocket is true)
  authToken?: string;
  walletAddress?: string;
  deviceID?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
}

/**
 * Hook for managing bucket collections data
 * Fetches buckets from API and calculates collection metrics
 */
export function useBucketCollections(
  config: BucketCollectionsConfig,
): {
  collections: BucketCollection[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  spotWalletBalance: number;
} {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [collections, setCollections] = useState<BucketCollection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [spotWalletBalance, setSpotWalletBalance] = useState<number>(0);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const refreshIntervalRef = useRef<number | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);
  const isSubscribedRef = useRef<boolean>(false);
  // Store config in ref to avoid dependency issues in callbacks
  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Transform bucket data to BucketCollection format
   * Calculates maintenanceMargin, nominalValue, and riskRatio
   * Handles both API format and WebSocket format
   */
  const transformBucketToCollection = useCallback(
    (bucket: any): BucketCollection => {
      const bucketId = String(bucket.bucketId || bucket.bucket_id || bucket.id || "");
      const marginMode = bucket.marginMode || bucket.margin_mode || bucket.margin_type || "CROSS";
      const marginType =
        marginMode === "ISOLATED" || marginMode === "Isolated" || marginMode === "ISOLATE"
          ? "Isolated"
          : "Cross";

      // Extract or calculate maintenance margin (in satoshi format)
      // WebSocket format uses: used_margin
      // API format uses: maintenanceMargin, maintenance_margin, usedMargin, used_margin
      const maintenanceMarginRaw =
        bucket.maintenanceMargin ||
        bucket.maintenance_margin ||
        bucket.usedMargin ||
        bucket.used_margin ||
        0;

      // Extract or calculate nominal value (total equity) (in satoshi format)
      // WebSocket format uses: total_equity
      // API format uses: nominalValue, nominal_value, totalEquity, total_equity, totalMargin, total_margin
      const nominalValueRaw =
        bucket.nominalValue ||
        bucket.nominal_value ||
        bucket.totalEquity ||
        bucket.total_equity ||
        bucket.totalMargin ||
        bucket.total_margin ||
        bucket.dedicated_margin || // WebSocket format
        0;

      // Convert from satoshi to decimal
      const maintenance = typeof maintenanceMarginRaw === 'string' || typeof maintenanceMarginRaw === 'number'
        ? fromSatoshi(maintenanceMarginRaw)
        : 0;
      const nominal = typeof nominalValueRaw === 'string' || typeof nominalValueRaw === 'number'
        ? fromSatoshi(nominalValueRaw)
        : 0;

      // Calculate risk ratio (maintenance margin / nominal value)
      const riskRatio = nominal > 0 ? Math.min(maintenance / nominal, 1) : 0;

      return {
        bucketId,
        marginType,
        maintenanceMargin: maintenance,
        nominalValue: nominal,
        riskRatio,
      };
    },
    [],
  );

  /**
   * Fetch spot wallet balance for USDC from API
   */
  const fetchSpotWalletBalance = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    if (!currentConfig.walletAddress || currentConfig.walletAddress.trim() === '') {
      setSpotWalletBalance(0);
      return;
    }

    try {
      // Get USDC balance using getBalanceDecimal with assetID
      const balance = await infoClientRef.current.getBalanceDecimal(
        currentConfig.walletAddress,
        "USDC" // Specify USDC asset
      );
      
      // The response should have 'available' field already converted from satoshi to decimal
      const available = balance?.available || 0;
      setSpotWalletBalance(available);
    } catch (err) {
      console.warn("Failed to fetch spot wallet balance (USDC):", err);
      setSpotWalletBalance(0);
    }
  }, []);

  /**
   * Fetch bucket collections from API
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchCollections = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    // If walletAddress is not provided, we can't fetch collections
    if (!currentConfig.walletAddress || currentConfig.walletAddress.trim() === '') {
      // Clear error and collections when address is not available
      setError(null);
      setCollections([]);
      setLoading(false);
      isInitialLoadRef.current = false;
      return;
    }

    setError(null);
    // Only show loading state on initial load, not on refreshes
    if (isInitialLoadRef.current) {
      setLoading(true);
    }

    try {
      // Fetch spot wallet balance in parallel
      fetchSpotWalletBalance();

      let bucketsData: any[] = [];

      // Try to fetch buckets from API
      if (currentConfig.walletAddress) {
        try {
          bucketsData = await infoClientRef.current.getBuckets(
            currentConfig.walletAddress,
          );
          if (!Array.isArray(bucketsData)) {
            bucketsData = [];
          }
        } catch (err) {
          console.warn("Failed to fetch buckets, attempting to derive from positions:", err);
          // Fallback: derive from positions if buckets endpoint fails
          try {
            const positions = await infoClientRef.current.getPositionsDecimal(
              currentConfig.walletAddress,
            );
            if (Array.isArray(positions)) {
              // Group positions by bucketId and aggregate
              interface BucketData {
                positions: any[];
                totalMargin: number;
                marginMode: string;
              }
              const bucketMap = new Map<string | number, BucketData>();

              positions.forEach((pos: any) => {
                const bucketId = pos.bucketId || pos.bucket_id || "default";
                const existing: BucketData = bucketMap.get(bucketId) || {
                  positions: [] as any[],
                  totalMargin: 0,
                  marginMode: pos.marginMode || pos.margin_mode || "CROSS",
                };

                existing.positions.push(pos);
                existing.totalMargin += pos.margin || 0;
                bucketMap.set(bucketId, existing);
              });

              // Convert to bucket format
              bucketsData = Array.from(bucketMap.entries()).map(
                ([bucketId, data]) => ({
                  bucketId,
                  marginMode: data.marginMode,
                  totalMargin: data.totalMargin,
                  maintenanceMargin: data.totalMargin * 0.1, // Estimate 10% maintenance
                  totalEquity: data.totalMargin,
                }),
              );
            }
          } catch (posErr) {
            throw new Error(
              `Failed to fetch buckets: ${err instanceof Error ? err.message : String(err)}. Also failed to derive from positions: ${posErr instanceof Error ? posErr.message : String(posErr)}`,
            );
          }
        }
      } else {
        throw new Error("walletAddress is required to fetch bucket collections");
      }

      // Transform buckets to collections
      const transformedCollections: BucketCollection[] = bucketsData.map(
        transformBucketToCollection,
      );

      setCollections(transformedCollections);
      isInitialLoadRef.current = false; // Mark initial load as complete
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load bucket collections";
      setError(errorMessage);
      console.error("Failed to fetch bucket collections:", err);
      isInitialLoadRef.current = false; // Mark initial load as complete even on error
    } finally {
      setLoading(false);
    }
  }, [transformBucketToCollection, fetchSpotWalletBalance]); // transformBucketToCollection is stable, no config dependencies

  /**
   * Handle bucket updates from WebSocket (buckets channel)
   * Backend sends full arrays under data.buckets for both initial snapshots and updates
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   */
  const handleBucketsUpdate = useCallback((data: any) => {
    // WebSocket format: { buckets: [...], total_buckets: number, address: string, timestamp: number }
    if (data.buckets && Array.isArray(data.buckets)) {
      const transformedCollections: BucketCollection[] = data.buckets.map(
        transformBucketToCollection,
      );
      // Compare with current collections to avoid unnecessary state updates
      setCollections(prev => {
        if (prev.length !== transformedCollections.length) {
          return transformedCollections;
        }
        // Deep comparison - only update if collections actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(transformedCollections);
        return prevStr === newStr ? prev : transformedCollections;
      });
      // Mark initial load as complete when we receive websocket data
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        setLoading(false);
      }
    }
  }, [transformBucketToCollection]);

  // Initialize client and fetch collections
  useEffect(() => {
    // Skip initialization if walletAddress is not available
    if (!config.walletAddress || config.walletAddress.trim() === '') {
      setLoading(false);
      setError(null);
      setCollections([]);
      // Clean up any existing connections
      if (subscriptionClientRef.current) {
        subscriptionClientRef.current.disconnect();
        subscriptionClientRef.current = null;
      }
      if (refreshIntervalRef.current !== null) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;
    isInitialLoadRef.current = true; // Reset initial load flag when config changes
    isSubscribedRef.current = false; // Reset subscription flag

    // Initial fetch from HTTP (always do this for fast initial load)
    fetchCollections();

    if (useWebSocket && config.wsURL) {
      // WebSocket mode: primary source
      const subscriptionClient = new SubscriptionClient({
        url: config.wsURL,
        authToken: config.authToken,
        walletAddress: config.walletAddress,
        deviceID: config.deviceID,
      });
      subscriptionClientRef.current = subscriptionClient;

      // Subscribe function that can be called on initial connect or reconnection
      const subscribe = () => {
        if (
          subscriptionClient.isConnected() &&
          !isSubscribedRef.current
        ) {
          subscriptionClient.subscribeBuckets(handleBucketsUpdate);
          isSubscribedRef.current = true;
        }
      };

      // Connect WebSocket and subscribe
      subscriptionClient
        .connect()
        .then(() => {
          subscribe();
        })
        .catch((err) => {
          console.error('Failed to connect WebSocket:', err);
          // Fallback to polling if WS fails
          if (config.refreshInterval && config.refreshInterval > 0) {
            refreshIntervalRef.current = window.setInterval(() => {
              fetchCollections();
            }, config.refreshInterval);
          }
        });

      // Monitor connection state changes and re-subscribe on reconnection
      let unsubscribeStateChange: (() => void) | null = null;
      const wsClient = (subscriptionClient as any).wsClient;
      if (wsClient) {
        unsubscribeStateChange = wsClient.onStateChange(
          (state: ConnectionState) => {
            if (state === ConnectionState.Connected) {
              // Re-subscribe if we were previously subscribed
              if (isSubscribedRef.current) {
                subscribe();
              }
              // Stop polling when WS is connected
              if (refreshIntervalRef.current !== null) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
              }
            } else {
              // Start polling when WS disconnects
              if (
                state === ConnectionState.Disconnected &&
                config.refreshInterval &&
                config.refreshInterval > 0 &&
                refreshIntervalRef.current === null
              ) {
                refreshIntervalRef.current = window.setInterval(() => {
                  fetchCollections();
                }, config.refreshInterval);
              }
            }
          },
        );
      }

      return () => {
        if (unsubscribeStateChange) {
          unsubscribeStateChange();
        }
        if (subscriptionClientRef.current) {
          subscriptionClientRef.current.unsubscribeBuckets(handleBucketsUpdate);
          subscriptionClientRef.current.disconnect();
          subscriptionClientRef.current = null;
        }
        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    } else {
      // Polling mode: set up auto-refresh if interval is specified
      if (config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(() => {
          fetchCollections();
        }, config.refreshInterval);
      }

      return () => {
        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.baseURL,
    config.wsURL,
    config.authToken,
    config.walletAddress,
    config.deviceID,
    config.refreshInterval,
    useWebSocket,
    handleBucketsUpdate,
    // Note: fetchCollections is intentionally NOT in dependencies
    // fetchCollections uses configRef to access latest config without causing dependency loops
    // The effect will re-run when walletAddress changes (included above), which is the desired behavior
  ]);

  return {
    collections,
    loading,
    error,
    refresh: fetchCollections,
    spotWalletBalance,
  };
}
